import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { RotateCcw, Save, ChevronDown, ChevronRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ax = axios.create({ withCredentials: true });

const AdminFrameworksTab = () => {
  const [frameworks, setFrameworks] = useState([]);
  const [principles, setPrinciples] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openKey, setOpenKey] = useState(null);
  const [edits, setEdits] = useState({}); // { frameworkKey: {name, criteria} }
  const [saving, setSaving] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await ax.get(`${API}/admin/evaluation-frameworks`);
      setFrameworks(data.frameworks || []);
      setPrinciples(data.principles || []);
      setEdits({});
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load frameworks.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const currentFor = (fw) => edits[fw.key] || {
    name: fw.name,
    criteria: (fw.criteria || []).map((c) => ({ ...c })),
  };

  const setEditName = (fw, name) => setEdits((e) => ({ ...e, [fw.key]: { ...currentFor(fw), name } }));
  const setEditCriterion = (fw, i, patch) => setEdits((e) => {
    const cur = currentFor(fw);
    const next = cur.criteria.map((c, idx) => idx === i ? { ...c, ...patch } : c);
    return { ...e, [fw.key]: { ...cur, criteria: next } };
  });

  const totalWeightFor = (fw) => currentFor(fw).criteria.reduce((a, c) => a + (parseFloat(c.weight) || 0), 0);

  const save = async (fw) => {
    const cur = currentFor(fw);
    const total = cur.criteria.reduce((a, c) => a + (parseFloat(c.weight) || 0), 0);
    if (Math.round(total * 100) / 100 !== 100) {
      toast.error(`Weights must sum to 100 (currently ${total.toFixed(2)}).`);
      return;
    }
    setSaving(fw.key);
    try {
      await ax.patch(`${API}/admin/evaluation-frameworks/${fw.key}`, {
        name: cur.name,
        criteria: cur.criteria.map((c) => ({ key: c.key, label: c.label, weight: parseFloat(c.weight) })),
      });
      toast.success(`${cur.name} updated.`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed.');
    } finally {
      setSaving(null);
    }
  };

  const reset = async (fw) => {
    if (!window.confirm(`Reset "${fw.name}" to factory defaults? Your edits for this framework will be lost.`)) return;
    setSaving(fw.key);
    try {
      await ax.post(`${API}/admin/evaluation-frameworks/${fw.key}/reset`);
      toast.success('Reset to defaults.');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Reset failed.');
    } finally {
      setSaving(null);
    }
  };

  return (
    <div data-testid="admin-frameworks-tab">
      <div className="mb-6">
        <h3 className="font-[Libre_Baskerville] text-2xl text-[var(--ink)]">Jury Evaluation Frameworks</h3>
        <p className="text-[var(--ink-muted)] text-sm mt-1">
          10 official pillar frameworks. Each nomination automatically loads the framework matching its award pillar.
          Weights must sum to 100 per framework.
        </p>
      </div>

      {/* General judging principles */}
      <div className="mb-8 border border-[var(--gold-faint)] bg-[var(--bg-alt)]/40 p-5">
        <div className="text-[10px] tracking-[0.3em] uppercase text-[var(--gold)] mb-3">General Judging Principles</div>
        <ul className="space-y-2 text-[var(--ink)] text-sm">
          {principles.map((p, i) => (
            <li key={i} className="flex items-start gap-3">
              <span className="numeral text-[var(--gold)] text-xs mt-0.5">{String(i + 1).padStart(2, '0')}</span>
              <span>{p}</span>
            </li>
          ))}
        </ul>
      </div>

      {loading ? (
        <div className="text-[var(--ink-muted)] text-xs tracking-[0.3em] uppercase">Loading…</div>
      ) : (
        <div className="space-y-3">
          {frameworks.map((fw) => {
            const cur = currentFor(fw);
            const total = totalWeightFor(fw);
            const totalOk = Math.round(total * 100) / 100 === 100;
            const dirty = JSON.stringify(cur) !== JSON.stringify({ name: fw.name, criteria: (fw.criteria || []).map((c) => ({ ...c })) });
            const isOpen = openKey === fw.key;
            return (
              <div key={fw.key} className="border border-[var(--gold-faint)]" data-testid={`framework-card-${fw.key}`}>
                <button
                  type="button"
                  onClick={() => setOpenKey(isOpen ? null : fw.key)}
                  className="w-full flex items-center justify-between p-4 hover:bg-[var(--gold)]/5 transition-colors"
                  data-testid={`framework-toggle-${fw.key}`}
                >
                  <div className="flex items-center gap-3">
                    {isOpen ? <ChevronDown size={16} className="text-[var(--gold)]" /> : <ChevronRight size={16} className="text-[var(--gold)]" />}
                    <div className="text-left">
                      <div className="font-[Libre_Baskerville] text-[var(--ink)] text-lg">{cur.name}</div>
                      <div className="text-[10px] tracking-[0.3em] uppercase text-[var(--ink-muted)] mt-1">
                        {cur.criteria.length} criteria · total weight {total.toFixed(0)}%
                      </div>
                    </div>
                  </div>
                  {dirty && <span className="text-[10px] tracking-[0.3em] uppercase text-[var(--terracotta)]">unsaved</span>}
                </button>
                {isOpen && (
                  <div className="p-5 border-t border-[var(--gold-faint)] space-y-4">
                    <label className="block">
                      <span className="text-[10px] tracking-[0.3em] uppercase text-[var(--ink-muted)]">Framework Name</span>
                      <input
                        type="text"
                        value={cur.name}
                        onChange={(e) => setEditName(fw, e.target.value)}
                        className="mt-2 w-full bg-[var(--bg)] border border-[var(--gold-faint)] focus:border-[var(--gold)] outline-none p-2 text-[var(--ink)]"
                        data-testid={`framework-name-${fw.key}`}
                      />
                    </label>

                    <div className="space-y-2">
                      <div className="grid grid-cols-[1fr_100px] gap-3 text-[10px] tracking-[0.3em] uppercase text-[var(--ink-muted)] px-1">
                        <div>Criterion Label</div>
                        <div className="text-right">Weight (%)</div>
                      </div>
                      {cur.criteria.map((c, i) => (
                        <div key={c.key} className="grid grid-cols-[1fr_100px] gap-3">
                          <input
                            type="text"
                            value={c.label}
                            onChange={(e) => setEditCriterion(fw, i, { label: e.target.value })}
                            className="bg-[var(--bg)] border border-[var(--gold-faint)] focus:border-[var(--gold)] outline-none p-2 text-[var(--ink)] text-sm"
                            data-testid={`criterion-label-${fw.key}-${c.key}`}
                          />
                          <input
                            type="number"
                            min="0"
                            max="100"
                            step="1"
                            value={c.weight}
                            onChange={(e) => setEditCriterion(fw, i, { weight: e.target.value })}
                            className="bg-[var(--bg)] border border-[var(--gold-faint)] focus:border-[var(--gold)] outline-none p-2 text-[var(--ink)] text-sm text-right numeral"
                            data-testid={`criterion-weight-${fw.key}-${c.key}`}
                          />
                        </div>
                      ))}
                      <div className={`flex items-center justify-between text-[11px] tracking-[0.2em] uppercase pt-2 border-t border-[var(--gold-faint)]/40 ${totalOk ? 'text-[var(--gold)]' : 'text-[var(--terracotta)]'}`}>
                        <span>Total Weight</span>
                        <span className="numeral" data-testid={`framework-total-${fw.key}`}>{total.toFixed(2)}%</span>
                      </div>
                    </div>

                    <div className="flex gap-3 pt-2">
                      <button
                        type="button"
                        onClick={() => save(fw)}
                        disabled={saving === fw.key || !totalOk || !dirty}
                        className="inline-flex items-center gap-2 bg-[var(--gold)] hover:bg-[var(--gold-bright)] disabled:opacity-40 text-[var(--bg)] text-[10px] tracking-[0.3em] uppercase px-4 py-2 transition-colors"
                        data-testid={`framework-save-${fw.key}`}
                      >
                        <Save size={12} /> {saving === fw.key ? 'Saving…' : 'Save Changes'}
                      </button>
                      <button
                        type="button"
                        onClick={() => reset(fw)}
                        disabled={saving === fw.key}
                        className="inline-flex items-center gap-2 border border-[var(--gold-faint)] hover:border-[var(--gold)] text-[var(--ink)] hover:text-[var(--gold)] text-[10px] tracking-[0.3em] uppercase px-4 py-2 transition-colors"
                        data-testid={`framework-reset-${fw.key}`}
                      >
                        <RotateCcw size={12} /> Reset to Defaults
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AdminFrameworksTab;
